import type { FC } from 'react';
import React, { useContext, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Manager, Popper, Reference } from '@atlaskit/popper';
import { IconButton } from '@atlaskit/button/new';
import HeadphonesIcon from '@atlaskit/icon/core/headphones';
import Tooltip from '@atlaskit/tooltip';

import type { ActiveAudioContent } from '@confluence/audio';
import { AudioBriefingPopupContent, useAudioBriefing } from '@confluence/audio';
import { useSessionData } from '@confluence/session-data';
import { ExperienceTrackerContext, AI_AUDIO_BRIEFING } from '@confluence/experience-tracker';
import { useAIRovoButtonEnablement } from '@confluence/ai-floating-context-menu/entry-points/useAIRovoButtonEnablement';

const i18n = defineMessages({
	listen: {
		id: 'object-sidebar-components.audio.button.tooltip',
		defaultMessage: 'Listen to briefing',
		description:
			'Message in tooltip which tells user that this button is tied to listen to a briefing',
	},
});

type AudioButtonProps = {
	contentId: string;
	contentType?: string | null;
	spaceKey?: string;
	contentTitle?: string | null;
};

export const AudioButton: FC<AudioButtonProps> = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
}) => {
	const { formatMessage, locale } = useIntl();

	const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

	const [{ activeContent: prevActiveContent, hasFetched, streamEnded, error }, { initialize }] =
		useAudioBriefing();

	const { cloudId, activationId } = useSessionData();

	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const activeContent: ActiveAudioContent = {
		id: contentId,
		// Should we just fail if there is no contentType?
		contentType: contentType ?? 'page',
		spaceKey,
		contentTitle,
	};

	const handleClick = () => {
		if (isPopupOpen) {
			handleOnClose();
		} else {
			setIsPopupOpen(true);
			initialize({
				activeContent,
				cloudId,
				activationId,
				locale,
			});
		}
	};

	const handleOnClose = () => {
		setIsPopupOpen(false);

		if (hasFetched && !streamEnded && !error) {
			experienceTracker.abort({
				name: AI_AUDIO_BRIEFING,
				reason: 'briefing popup was closed',
			});
		}
	};

	// Close popper if the contentId changes meaning the user has navigated to different content
	if (isPopupOpen && prevActiveContent?.id !== contentId) {
		handleOnClose();
	}

	return (
		<Manager>
			<Reference>
				{({ ref }) => (
					<Tooltip content={formatMessage(i18n.listen)} position="left" hideTooltipOnClick>
						<IconButton
							label={formatMessage(i18n.listen)}
							appearance="subtle"
							icon={HeadphonesIcon}
							shape={isRovoButtonEnabled ? 'default' : 'circle'}
							onClick={handleClick}
							testId="audio-button"
							ref={ref}
						/>
					</Tooltip>
				)}
			</Reference>
			{isPopupOpen && (
				<Popper placement="left-end" offset={[0, 16]}>
					{({ ref, style }) => (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						<div ref={ref} style={style}>
							<AudioBriefingPopupContent closePopup={handleOnClose} />
						</div>
					)}
				</Popper>
			)}
		</Manager>
	);
};
